import * as THREE from "three";

import type { SceneCamera, ScenePhoto, Vector2, Vector3 } from "./types";

const EPSILON = 1e-12;

export function projectPhotoPoint(
  point: Vector3,
  photo: ScenePhoto,
): Vector2 | null {
  const local = new THREE.Vector3()
    .fromArray(point)
    .sub(new THREE.Vector3().fromArray(photo.camera.position))
    .applyMatrix3(rodriguesMatrix(photo.camera.rotationRodrigues));
  if (local.z <= EPSILON) return null;
  return [
    (photo.camera.focalPx * local.x) / local.z +
      photo.camera.principalPoint[0] * photo.width,
    (photo.camera.focalPx * local.y) / local.z +
      photo.camera.principalPoint[1] * photo.height,
  ];
}

export function rodriguesMatrix(rotation: readonly number[]): THREE.Matrix3 {
  const vector = new THREE.Vector3(rotation[0], rotation[1], rotation[2]);
  const angle = vector.length();
  if (angle < EPSILON) return new THREE.Matrix3().identity();

  const [x, y, z] = vector.multiplyScalar(1 / angle).toArray();
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  const oneMinusCosine = 1 - cosine;
  return new THREE.Matrix3().set(
    cosine + x * x * oneMinusCosine,
    x * y * oneMinusCosine - z * sine,
    x * z * oneMinusCosine + y * sine,
    y * x * oneMinusCosine + z * sine,
    cosine + y * y * oneMinusCosine,
    y * z * oneMinusCosine - x * sine,
    z * x * oneMinusCosine - y * sine,
    z * y * oneMinusCosine + x * sine,
    cosine + z * z * oneMinusCosine,
  );
}

export function fittedCameraQuaternion(camera: SceneCamera): THREE.Quaternion {
  const elements = rodriguesMatrix(camera.rotationRodrigues).elements;
  // Matrix3 elements are column-major. Rows of world-to-camera R are the
  // world-space CV right/down/forward axes. Three cameras use right/up/back.
  const right = new THREE.Vector3(elements[0], elements[3], elements[6]);
  const up = new THREE.Vector3(-elements[1], -elements[4], -elements[7]);
  const back = new THREE.Vector3(-elements[2], -elements[5], -elements[8]);
  return new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(right, up, back),
  );
}

export function fittedVerticalFov(
  photo: ScenePhoto,
  viewportAspect: number,
): number {
  const imageAspect = photo.width / photo.height;
  const fittedHeightPx =
    viewportAspect >= imageAspect ? photo.height : photo.width / viewportAspect;
  return THREE.MathUtils.radToDeg(
    2 * Math.atan(fittedHeightPx / (2 * photo.camera.focalPx)),
  );
}

export function photoPlaneSize(
  photo: ScenePhoto,
  distance: number,
): [number, number] {
  return [
    (photo.width * distance) / photo.camera.focalPx,
    (photo.height * distance) / photo.camera.focalPx,
  ];
}

export function cameraForward(camera: SceneCamera): THREE.Vector3 {
  return new THREE.Vector3(0, 0, -1).applyQuaternion(
    fittedCameraQuaternion(camera),
  );
}

export function configureFittedCamera(
  target: THREE.PerspectiveCamera,
  photo: ScenePhoto,
  viewportAspect: number,
): void {
  target.position.fromArray(photo.camera.position);
  target.quaternion.copy(fittedCameraQuaternion(photo.camera));
  target.aspect = viewportAspect;
  target.fov = fittedVerticalFov(photo, viewportAspect);
  target.updateProjectionMatrix();
  target.updateMatrixWorld(true);
}
